import React, { useEffect, useState, useRef } from 'react';
import { graphql, Link } from 'gatsby';
import Helmet from 'react-helmet';
import SEO from '../components/SEO';
import Layout from '../components/Layout';

const Home = (props) => {
  const intro = props.data.intro;
  const site = props.data.site.siteMetadata;
  const volunteer = props.data.volunteer;
  const explainer = props.data.explainer;
  const volunteerOptions = props.data.volunteerOptions.edges;
  const explainerSteps = props.data.explainerSteps.edges;
  const contactJson = props.data.contactJson;
  const books = props.data.stats.books;
  const stats = props.data.stats;
  const testimonials = props.data.allTestimonialsJson?.edges?.map(({ node }) => node) || [];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerSlide, setCardsPerSlide] = useState(1);
  const [cardHeight, setCardHeight] = useState('auto');
  const cardRefs = useRef([]);

  // Calculate total slides based on cards per slide
  const totalSlides = Math.ceil(testimonials.length / cardsPerSlide);

  const introImageClasses = `intro-image ${
    intro.frontmatter.intro_image_absolute && 'intro-image-absolute'
  } ${intro.frontmatter.intro_image_hide_on_mobile && 'intro-image-hide-mobile'}`;

  useEffect(() => {
    const textPath = document.getElementById('marquee-text');
    const path = document.getElementById('text-path');
    const pathLength = path.getTotalLength();
    textPath.innerHTML = `<a 
        href="#"
        target="_blank"
        style="text-decoration: none;"
      >
        Be The Change
        &nbsp;&nbsp;&nbsp;&nbsp;
        Hidden Voices
        &nbsp;&nbsp;&nbsp;&nbsp;
      </a>`.repeat(20);

    let offset = 0;

    const animateText = () => {
      offset -= -1;
      if (offset < 0) {
        offset = pathLength;
      } else if (offset > pathLength) {
        offset = 0;
      }
      textPath.setAttribute('startOffset', `${offset}px`);
      requestAnimationFrame(animateText);
    };
    animateText();
  }, []);

  // Handle window resize to update cards per slide
  useEffect(() => {
    const updateCardsPerSlide = () => {
      if (typeof window !== 'undefined') {
        const isDesktop = window.innerWidth >= 768;
        setCardsPerSlide(isDesktop ? 2 : 1);
        // Reset to first slide when switching between mobile/desktop
        setCurrentSlide(0);
      }
    };

    // Set initial value
    updateCardsPerSlide();

    window.addEventListener('resize', updateCardsPerSlide);
    return () => window.removeEventListener('resize', updateCardsPerSlide);
  }, []);

  // Calculate and set equal height for all cards across all slides
  useEffect(() => {
    if (testimonials.length === 0 || cardRefs.current.length === 0) return;

    const calculateMaxHeight = () => {
      let maxHeight = 0;
      let allRefsReady = true;

      // Check if all refs are ready
      cardRefs.current.forEach((ref) => {
        if (!ref) {
          allRefsReady = false;
        }
      });

      if (!allRefsReady) {
        // Retry after a short delay
        setTimeout(calculateMaxHeight, 100);
        return;
      }

      // First, reset all heights to auto to measure natural height
      cardRefs.current.forEach((ref) => {
        if (ref) {
          ref.style.height = 'auto';
        }
      });

      // Force a reflow
      void document.body.offsetHeight;

      // Now measure all cards
      cardRefs.current.forEach((ref) => {
        if (ref) {
          const height = ref.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });

      // Set all cards to the maximum height
      if (maxHeight > 0) {
        cardRefs.current.forEach((ref) => {
          if (ref) {
            ref.style.height = `${maxHeight}px`;
          }
        });
        setCardHeight(`${maxHeight}px`);
      }
    };

    // Wait for DOM to be ready and all cards to be rendered
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(calculateMaxHeight);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [testimonials, cardsPerSlide]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <Layout bodyClass="page-home">
      <SEO title={site.title} />
      <Helmet>
        <meta name="description" content="" />
      </Helmet>

      {/* intro section */}
      <div className="intro">
        <div className="container">
          <div className="row justify-content-start">
            <div className="col-12 col-md-5 col-lg-5 order-2 order-md-1 align-self-center">
              <div className="tag">{intro.frontmatter.tag_text}</div>
              <div dangerouslySetInnerHTML={{ __html: intro.html }} />
              <div>
                <div className="call-box-bottom">
                  <a href={'/donate'} className="button">
                    Donate
                  </a>
                  <a className="ml-2" href={contactJson.contact_button_link}>
                    Volunteer
                  </a>
                </div>
              </div>
            </div>
            {intro.frontmatter.intro_image && (
              <div className="col-12 col-md-7 col-lg-7 order-1 order-md-2 position-relative">
                <img
                  alt={intro.frontmatter.intro_image_alt}
                  className={introImageClasses}
                  src={intro.frontmatter.intro_image}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Curved marquee section */}
      <div className="marquee-container">
        <svg width="100%" height="20rem" viewBox="0 0 1000 100">
          <defs>
            <path
              id="text-path"
              d="M -50 50 Q 200 -50, 400 50 Q 600 150, 800 50 Q 1000 -50, 1200 50"
              fill="transparent"
            />
          </defs>
          {/* stripe */}
          <path
            d="M -50 50 Q 200 -50, 400 50 Q 600 150, 800 50 Q 1000 -50, 1200 50"
            fill="none"
            stroke="#d8d8ff"
            strokeWidth="0.2em"
          />
          {/* text */}
          <text
            fontSize="0.8rem"
            fill="#020203"
            fontFamily="Rubik, sans-serif"
            className="uppercase-text"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            <textPath id="marquee-text" href="#text-path"></textPath>
          </text>
        </svg>
      </div>

      {/* What we do section */}
      <div className="strip">
        <div className="container pt-6 pb-6 pb-md-10">
          <div className="mb-4" dangerouslySetInnerHTML={{ __html: explainer.html }} />
          <div className="row justify-content-start">
            {explainerSteps.map(({ node }) => (
              <div key={node.id} className="col-12 col-md-4 mb-1">
                <img style={{ width: 'inherit' }} src={node.frontmatter.image} />
                <h2>{node.frontmatter.title} </h2>
                <div dangerouslySetInnerHTML={{ __html: node.html }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="strip strip-green stats">
        <div className="container pt-6 pb-6 pt-md-10 pb-md-10">
          <div className="row">
            <div className="col-12 col-md-6 mb-1 overflow-hidden">
              {Object.keys(books).map((category, index) => (
                <div key={index} className="scroll" style={{ '--t': '40s' }}>
                  <div>
                    {books[category].map((book, bookIndex) => (
                      <span key={bookIndex}>{book}</span>
                    ))}
                  </div>
                  <div>
                    {books[category].map((book, bookIndex) => (
                      <span key={bookIndex}>{book}</span>
                    ))}
                  </div>
                </div>
              ))}
              <img className="person-listening" src="/images/illustrations/bottom-banner001.png" />
            </div>
            <div className="col-12 col-md-6 mb-1 px-2 stat">
              <h1>{stats.title}</h1>
              <p>{stats.slug}</p>
              <div className="stat-container">
                {stats.stats.map(({ stat, subText }) => (
                  <div key={stat} className="stat-body">
                    <h1>{stat}+</h1>
                    <p>{subText}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <div className="strip testimonials-section">
          <div className="container pt-3 pb-3 pt-md-5 pb-md-5">
            <h2 className="text-center mb-5">Testimonials</h2>
            <div className="testimonials-slider">
              <div className="testimonials-slider-wrapper">
                <div
                  className="testimonials-slider-track"
                  style={{
                    transform: `translateX(-${currentSlide * 100}%)`
                  }}
                >
                  {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                    <div key={slideIndex} className="testimonial-slide">
                      <div className="testimonial-slide-content">
                        {testimonials
                          .slice(
                            slideIndex * cardsPerSlide,
                            slideIndex * cardsPerSlide + cardsPerSlide
                          )
                          .map((testimonial, cardIndex) => {
                            const cardRefIndex = slideIndex * cardsPerSlide + cardIndex;
                            return (
                              <div key={testimonial.id} className="testimonial-card-wrapper">
                                <div
                                  ref={(el) => {
                                    if (el) {
                                      cardRefs.current[cardRefIndex] = el;
                                    }
                                  }}
                                  className="testimonial-card"
                                  style={{ height: cardHeight !== 'auto' ? cardHeight : 'auto' }}
                                >
                                  <div className="testimonial-quote-icon">
                                    <div className="quote-icon-circle">
                                      <span className="quote-mark">"</span>
                                    </div>
                                  </div>
                                  <p className="testimonial-text">{testimonial.text}</p>
                                  <div className="testimonial-author">
                                    <img
                                      src={testimonial.image || '/images/team/default.png'}
                                      alt={testimonial.author}
                                      className="testimonial-author-image"
                                    />
                                    <div className="testimonial-author-info">
                                      <div className="testimonial-author-name">
                                        {testimonial.author}
                                      </div>
                                      <div className="testimonial-author-title">
                                        {testimonial.title}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Dots */}
              {totalSlides > 1 && (
                <div className="testimonial-dots">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`testimonial-dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Volunteer Section */}
      {volunteerOptions.length > 0 && (
        <div className="strip" id="volunteer">
          <div className="container pt-6 pb-6 pb-md-10">
            <div className="mb-4" dangerouslySetInnerHTML={{ __html: volunteer.html }} />
            <div className="row justify-content-start">
              {volunteerOptions.map(({ node }) => (
                <div key={node.id} className="col-12 col-md-4 mb-1">
                  <h2 className="d-flex flex-column">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        background: `url("${node.frontmatter.blob}") 50% no-repeat`,
                        height: 90,
                        width: 90
                      }}
                    >
                      <span
                        className={`fas fa-${node.frontmatter.icon} mr-1 mt-1`}
                        style={{ color: '#fff', fontSize: '2rem' }}
                      ></span>
                    </div>
                    <Link to={'/'}>{node.frontmatter.title}</Link>
                  </h2>
                  <div dangerouslySetInnerHTML={{ __html: node.html }} />
                </div>
              ))}
            </div>
            <div className="row justify-content-center">
              <Link className="button button-primary mt-2" to="/contact/">
                Contact us today
              </Link>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export const query = graphql`
  query {
    intro: markdownRemark(fileAbsolutePath: { regex: "/content/index.md/" }) {
      html
      frontmatter {
        image
        intro_image
        intro_image_alt
        tag_text
        intro_image_absolute
        intro_image_hide_on_mobile
        title
      }
    }

    volunteer: markdownRemark(fileAbsolutePath: { regex: "/content/volunteer.md/" }) {
      html
    }

    volunteerOptions: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/volunteer/.*/" } }
      sort: { fields: [frontmatter___weight], order: ASC }
      limit: 6
    ) {
      edges {
        node {
          id
          frontmatter {
            title
            blob
            icon
          }
          html
        }
      }
    }

    stats: statsJson {
      title
      slug
      stats {
        stat
        subText
      }
      books {
        fiction
        history
        literature
        philosophy
      }
    }

    explainer: markdownRemark(fileAbsolutePath: { regex: "/content/explainer.md/" }) {
      html
    }

    explainerSteps: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/explainer/.*/" } }
      sort: { fields: [frontmatter___weight], order: ASC }
      limit: 6
    ) {
      edges {
        node {
          id
          frontmatter {
            title
            image
          }
          html
        }
      }
    }

    features: allFeaturesJson {
      edges {
        node {
          id
          title
          description
          image
        }
      }
    }

    site {
      siteMetadata {
        title
      }
    }

    contactJson {
      phone
      email
      contact_button_link
    }

    allTestimonialsJson {
      edges {
        node {
          id
          text
          author
          title
          image
        }
      }
    }
  }
`;

export default Home;
